import { useId, type FormEvent } from "react";
import type { MoneyAccount, PersonOwed, ReimburseInput } from "@/api/cash";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { formatMoney } from "@/lib/format";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type ReimburseFormProps = {
  person: PersonOwed;
  /** The company's money accounts; a person's own account is never one to pay from. */
  accounts: MoneyAccount[];
  onSubmit: (input: ReimburseInput) => Promise<unknown>;
  onCancel: () => void;
};

/** Paying a person back for the bills they paid: all that is owed, or less for a part. */
export function ReimburseForm({ person, accounts, onSubmit, onCancel }: ReimburseFormProps) {
  const id = useId();
  const sources = accounts.filter((a) => !a.archived_at && a.kind !== "person");
  const { values, set } = useFormValues({
    made_on: todayIso(),
    from_account_id: "",
    amount: person.owed,
    received_amount: person.owed,
    reference: "",
    notes: "",
  });
  const { error, errors, saving, run } = useSubmitState();
  const source = sources.find((a) => a.id === values.from_account_id);
  const currency = source?.currency ?? person.currency;
  const crossCurrency = currency !== person.currency;

  /** What is owed is in the person's currency: it is no amount in another one. */
  function chooseSource(accountId: string): void {
    set("from_account_id", accountId);
    const chosen = sources.find((a) => a.id === accountId);
    set("amount", !chosen || chosen.currency === person.currency ? person.owed : "");
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        made_on: values.made_on,
        from_account_id: values.from_account_id,
        amount: values.amount.trim(),
        received_amount: crossCurrency ? values.received_amount.trim() || null : null,
        reference: values.reference.trim(),
        notes: values.notes.trim(),
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} />
      <p className="text-sm text-muted-foreground">
        PBO owes {person.name} {formatMoney(person.owed, person.currency)}. The repayment settles
        their bills oldest first; a bill it covers in full is marked reimbursed.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-on`}
          label="Paid on"
          type="date"
          value={values.made_on}
          onChange={(value) => set("made_on", value)}
          error={errors.made_on}
        />
        <SelectField
          id={`${id}-from`}
          label="Paid from"
          value={values.from_account_id}
          onChange={chooseSource}
          options={sources.map((a) => ({ value: a.id, label: `${a.name} (${a.currency})` }))}
          placeholder="Choose an account"
          error={errors.from_account_id}
        />
        <TextField
          id={`${id}-amount`}
          label={`Amount (${currency})`}
          inputMode="decimal"
          value={values.amount}
          onChange={(value) => set("amount", value)}
          error={errors.amount}
          hint={crossCurrency ? undefined : "Less than all of it for a part-payment"}
        />
        {crossCurrency && (
          <TextField
            id={`${id}-received`}
            label={`${person.name} receives (${person.currency})`}
            inputMode="decimal"
            value={values.received_amount}
            onChange={(value) => set("received_amount", value)}
            error={errors.received_amount}
            hint="What reached them; nothing is converted at a guessed rate"
          />
        )}
        <TextField
          id={`${id}-reference`}
          label="Reference"
          value={values.reference}
          onChange={(value) => set("reference", value)}
          error={errors.reference}
          hint="UTR or UPI reference, optional"
        />
      </div>
      <TextAreaField
        id={`${id}-notes`}
        label="Notes"
        value={values.notes}
        onChange={(value) => set("notes", value)}
        error={errors.notes}
        rows={2}
      />
      <FormActions
        saving={saving}
        submitLabel="Reimburse"
        onCancel={onCancel}
        disabled={!values.from_account_id}
      />
    </form>
  );
}
